import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"

import { BrandMark } from "@/components/layout/BrandMark"
import { dashboardPath } from "@/features/auth/panel-routes"
import { usePanelStore } from "@/features/auth/panel-store"
import type { Panel } from "@/features/auth/schemas"
import { useAuthStore } from "@/features/auth/store"
import { cn } from "@/lib/utils/cn"

/**
 * Çift panel sahibi kullanıcılara gösterilen seçim ekranı.
 *  - Auth yoksa /login
 *  - Tek/sıfır panel ise gerekli yönlendirme (login'e veya panel dashboard'una)
 *  - Aktif panel zaten set ise direkt o panele git (geri tuşu ile dönenler için)
 */
export const Route = createFileRoute("/select-panel")({
  beforeLoad: () => {
    const auth = useAuthStore.getState()
    if (!auth.isAuthenticated()) {
      throw redirect({ to: "/login" })
    }
    const panels = auth.user?.panels ?? []
    if (panels.length === 0) {
      auth.clear()
      throw redirect({ to: "/login" })
    }
    const active = usePanelStore.getState().active
    if (active && panels.includes(active)) {
      throw redirect({ to: dashboardPath(active) })
    }
    const [first] = panels
    if (panels.length === 1 && first) {
      usePanelStore.getState().setActive(first)
      throw redirect({ to: dashboardPath(first) })
    }
    // panels.length > 1 → seçim ekranı görüntülenir
  },
  component: SelectPanelPage,
})

interface PanelOption {
  panel: Panel
  labelKey: string
  descriptionKey: string
  brand: string
  accentClass: string
}

const PANEL_OPTIONS: PanelOption[] = [
  {
    panel: "hems",
    labelKey: "selectPanel.panels.hems.label",
    descriptionKey: "selectPanel.panels.hems.description",
    brand: "n2n",
    accentClass: "bg-ok-primary/10 text-ok-primary",
  },
  {
    panel: "cni",
    labelKey: "selectPanel.panels.cni.label",
    descriptionKey: "selectPanel.panels.cni.description",
    brand: "n2n",
    accentClass: "bg-[hsl(220_9%_18%/0.08)] text-[hsl(220_9%_18%)]",
  },
]

function SelectPanelPage() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const userPanels = useAuthStore((s) => s.user?.panels ?? [])

  const handleSelect = async (panel: Panel) => {
    usePanelStore.getState().setActive(panel)
    await navigate({ to: dashboardPath(panel), replace: true })
  }

  const visibleOptions = PANEL_OPTIONS.filter((opt) => userPanels.includes(opt.panel))

  return (
    <div className="min-h-screen grid place-items-center bg-ok-bg p-6">
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center space-y-3">
          <BrandMark size="lg" panel={null} className="block" />
          <h1 className="text-2xl font-semibold tracking-tight">{t("selectPanel.title")}</h1>
          <p className="text-sm text-ok-foreground-muted">{t("selectPanel.subtitle")}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {visibleOptions.map((opt) => (
            <button
              key={opt.panel}
              type="button"
              onClick={() => {
                void handleSelect(opt.panel)
              }}
              className={cn(
                "group rounded-ok-lg border border-ok-border bg-ok-bg-subtle p-6",
                "text-left flex flex-col gap-3 shadow-ok-sm",
                "transition-all duration-[var(--ok-duration)] ease-[var(--ok-ease)]",
                "hover:border-ok-border-strong hover:shadow-ok hover:-translate-y-0.5",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ok-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ok-bg",
              )}
            >
              <div
                className={cn(
                  "inline-flex h-10 w-fit items-center px-3 rounded-ok text-sm font-bold tracking-tight",
                  opt.accentClass,
                )}
                style={{ fontFamily: "Figtree, 'Figtree Fallback', system-ui, sans-serif" }}
              >
                {opt.brand}
              </div>
              <div className="space-y-1">
                <div className="text-base font-semibold text-ok-foreground">
                  {t(opt.labelKey)}
                </div>
                <div className="text-sm text-ok-foreground-muted">
                  {t(opt.descriptionKey)}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
